/**
 * Custom TipTap Image Extension with size, alignment, caption, and link controls.
 * Shows a floating toolbar when an image is selected.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from '@tiptap/react';
import {
  AlignLeft, AlignCenter, AlignRight, Maximize2, Minimize2,
  RectangleHorizontal, Link2, Type, Trash2, X,
} from 'lucide-react';

// ── Custom Image Node ──

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blogImage: {
      setBlogImage: (options: { src: string; alt?: string; title?: string }) => ReturnType;
    };
  }
}

export const BlogImage = Node.create({
  name: 'blogImage',
  group: 'block',
  atom: false,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
      title: { default: '' },
      size: { default: 'full' },        // 'small' (33%), 'medium' (50%), 'full' (100%)
      alignment: { default: 'center' },  // 'left', 'center', 'right'
      caption: { default: '' },
      href: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'figure[data-blog-image]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, alt, size, alignment, caption, href, ...rest } = HTMLAttributes;
    const sizeClass = `blog-img--${size || 'full'}`;
    const alignClass = `blog-img--${alignment || 'center'}`;
    const figureAttrs = mergeAttributes(rest, {
      'data-blog-image': '',
      class: `blog-img-figure ${sizeClass} ${alignClass}`,
    });

    const img = ['img', { src, alt: alt || '' }];
    const children: any[] = href ? [['a', { href, target: '_blank', rel: 'noopener noreferrer' }, img]] : [img];
    if (caption) {
      children.push(['figcaption', {}, caption]);
    }
    return ['figure', figureAttrs, ...children];
  },

  addCommands() {
    return {
      setBlogImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlogImageView);
  },
});

// ── Size/Alignment Presets ──

const SIZE_OPTIONS = [
  { value: 'small', label: '33%', icon: Minimize2 },
  { value: 'medium', label: '50%', icon: RectangleHorizontal },
  { value: 'full', label: '100%', icon: Maximize2 },
] as const;

const ALIGN_OPTIONS = [
  { value: 'left', icon: AlignLeft },
  { value: 'center', icon: AlignCenter },
  { value: 'right', icon: AlignRight },
] as const;

// ── Node View Component (with floating toolbar) ──

const BlogImageView: React.FC<any> = ({ node, updateAttributes, deleteNode, selected }) => {
  const { src, alt, size, alignment, caption, href } = node.attrs;
  const [showToolbar, setShowToolbar] = useState(false);
  const [editingCaption, setEditingCaption] = useState(false);
  const [editingLink, setEditingLink] = useState(false);
  const [editingAlt, setEditingAlt] = useState(false);
  const [linkValue, setLinkValue] = useState(href || '');
  const [altValue, setAltValue] = useState(alt || '');

  useEffect(() => {
    setShowToolbar(selected);
  }, [selected]);

  const sizeWidth = size === 'small' ? '33%' : size === 'medium' ? '50%' : '100%';
  const floatStyle: React.CSSProperties =
    alignment === 'left' ? { float: 'left', marginRight: '24px', marginBottom: '16px' }
    : alignment === 'right' ? { float: 'right', marginLeft: '24px', marginBottom: '16px' }
    : { margin: '24px auto', display: 'block' };

  const wrapperStyle: React.CSSProperties = {
    ...floatStyle,
    maxWidth: sizeWidth,
    width: sizeWidth,
    position: 'relative',
    cursor: 'pointer',
  };

  const handleSaveLink = () => {
    updateAttributes({ href: linkValue.trim() || '' });
    setEditingLink(false);
  };

  const handleSaveAlt = () => {
    updateAttributes({ alt: altValue.trim() });
    setEditingAlt(false);
  };

  return (
    <NodeViewWrapper>
      <figure
        style={wrapperStyle}
        onClick={() => setShowToolbar(true)}
        data-blog-image=""
      >
        {/* Floating Toolbar */}
        {showToolbar && (
          <div style={toolbarStyle}>
            {/* Size buttons */}
            <ToolbarGroup label="Size">
              {SIZE_OPTIONS.map(opt => (
                <ToolBtn
                  key={opt.value}
                  active={size === opt.value}
                  onClick={() => updateAttributes({ size: opt.value })}
                  title={opt.label}
                >
                  <opt.icon size={14} />
                </ToolBtn>
              ))}
            </ToolbarGroup>

            <ToolbarDivider />

            {/* Alignment buttons */}
            <ToolbarGroup label="Align">
              {ALIGN_OPTIONS.map(opt => (
                <ToolBtn
                  key={opt.value}
                  active={alignment === opt.value}
                  onClick={() => updateAttributes({ alignment: opt.value })}
                  title={opt.value}
                >
                  <opt.icon size={14} />
                </ToolBtn>
              ))}
            </ToolbarGroup>

            <ToolbarDivider />

            {/* Caption toggle */}
            <ToolBtn
              active={editingCaption}
              onClick={() => setEditingCaption(!editingCaption)}
              title="Caption"
            >
              <Type size={14} />
            </ToolBtn>

            {/* Link toggle */}
            <ToolBtn
              active={editingLink || !!href}
              onClick={() => { setEditingLink(!editingLink); setLinkValue(href || ''); }}
              title="Link"
            >
              <Link2 size={14} />
            </ToolBtn>

            {/* Alt text */}
            <ToolBtn
              active={editingAlt}
              onClick={() => { setEditingAlt(!editingAlt); setAltValue(alt || ''); }}
              title="Alt text"
            >
              Alt
            </ToolBtn>

            <ToolbarDivider />

            {/* Delete */}
            <ToolBtn onClick={deleteNode} title="Remove image">
              <Trash2 size={14} />
            </ToolBtn>
          </div>
        )}

        {/* Image */}
        <img
          src={src}
          alt={alt || ''}
          style={{
            width: '100%',
            borderRadius: '8px',
            display: 'block',
            outline: selected ? '2px solid #2E5E34' : 'none',
            outlineOffset: '2px',
          }}
          draggable={false}
        />

        {/* Caption input */}
        {(editingCaption || caption) && (
          <figcaption style={{ textAlign: 'center', marginTop: '8px' }}>
            <input
              type="text"
              value={caption}
              onChange={e => updateAttributes({ caption: e.target.value })}
              placeholder="Add a caption..."
              style={{
                width: '100%', textAlign: 'center', border: 'none', outline: 'none',
                fontSize: '13px', color: '#6b7280', fontStyle: 'italic', background: 'transparent',
                padding: '4px 0',
              }}
              onFocus={() => setEditingCaption(true)}
            />
          </figcaption>
        )}

        {/* Link editor popover */}
        {editingLink && (
          <div style={popoverStyle}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <Link2 size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
              <input
                type="url"
                value={linkValue}
                onChange={e => setLinkValue(e.target.value)}
                placeholder="https://..."
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleSaveLink()}
                style={{ flex: 1, padding: '4px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', outline: 'none' }}
              />
              <button onClick={handleSaveLink} style={popoverBtnStyle}>Save</button>
              {href && (
                <button onClick={() => { updateAttributes({ href: '' }); setEditingLink(false); }} style={{ ...popoverBtnStyle, color: '#dc2626' }}>
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Alt text editor popover */}
        {editingAlt && (
          <div style={popoverStyle}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', flexShrink: 0 }}>Alt:</span>
              <input
                type="text"
                value={altValue}
                onChange={e => setAltValue(e.target.value)}
                placeholder="Describe the image..."
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleSaveAlt()}
                style={{ flex: 1, padding: '4px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', outline: 'none' }}
              />
              <button onClick={handleSaveAlt} style={popoverBtnStyle}>Save</button>
            </div>
          </div>
        )}
      </figure>
    </NodeViewWrapper>
  );
};

// ── Toolbar Sub-components ──

const ToolbarGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', gap: '2px' }}>{children}</div>
);

const ToolBtn: React.FC<{
  onClick: () => void;
  title: string;
  active?: boolean;
  children: React.ReactNode;
}> = ({ onClick, title, active, children }) => (
  <button
    onClick={e => { e.stopPropagation(); onClick(); }}
    title={title}
    style={{
      padding: '4px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer',
      background: active ? '#2E5E34' : 'transparent',
      color: active ? 'white' : '#374151',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: 600, minWidth: '24px',
    }}
  >
    {children}
  </button>
);

const ToolbarDivider: React.FC = () => (
  <div style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
);

// ── Styles ──

const toolbarStyle: React.CSSProperties = {
  position: 'absolute',
  top: '-44px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '6px 8px',
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  zIndex: 10,
  whiteSpace: 'nowrap',
};

const popoverStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '-48px',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '8px 12px',
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  zIndex: 10,
  minWidth: '280px',
};

const popoverBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#2E5E34',
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
};
